import parse, { domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import { useMemo } from 'react';
import { ISitePage, ITextWebPart } from 'services/Graph/SharepointInterfaces';
import logger from 'services/Logging/logService';
import { sanitizeHtml } from 'utils/html';
import { resolveSharePointLinkUrl } from 'utils/url';
import { SharePointInlineImage } from './SharePointInlineImage';

interface ISharePointTextWebPartProps {
  webPart: ITextWebPart;
  enableLinks?: boolean;
  previewLinks?: boolean;
  pageContent?: ISitePage;
  siteId?: string;
}

const renderInlineImage = (
  node: Element,
  pageContent: ISitePage,
  siteId: string,
): JSX.Element | undefined => {
  // <img> tag: prefer src unless it's a data URI placeholder, fall back to data-imageurl
  if (node.name === 'img') {
    const src = node.attribs.src;
    const imageSource = src && !src.startsWith('data:') ? src : node.attribs['data-imageurl'];
    if (!imageSource) return undefined;

    return (
      <SharePointInlineImage
        src={imageSource}
        pageContent={pageContent}
        siteId={siteId}
        alt={node.attribs.alt}
        width={node.attribs['data-imagenaturalwidth'] || node.attribs.width}
        height={node.attribs['data-imagenaturalheight'] || node.attribs.height}
        alignment={node.attribs['data-alignment']}
      />
    );
  }

  // <div class="imagePlugin"> — how SharePoint stores Ctrl+V pasted images in text web parts
  if (node.name === 'div' && node.attribs.class?.includes('imagePlugin')) {
    const imageUrl = node.attribs['data-imageurl'];
    if (!imageUrl) return undefined;

    return (
      <SharePointInlineImage
        src={imageUrl}
        pageContent={pageContent}
        siteId={siteId}
        listId={node.attribs['data-listid']}
        width={node.attribs['data-imagenaturalwidth']}
        height={node.attribs['data-imagenaturalheight']}
        alignment={node.attribs['data-alignment']}
      />
    );
  }

  return undefined;
};

export const SharePointTextWebPart = (props: ISharePointTextWebPartProps) => {
  //Replace all links <a> with <span> so the user cannot click them or, when enabled, change all links
  //to open in a new window and use the fully qualified url
  const sharePointOptions: HTMLReactParserOptions = useMemo(
    () => ({
      replace: (domNode) => {
        const node = domNode as Element;
        if (node.name === 'a') {
          if (props.enableLinks) {
            const url = resolveSharePointLinkUrl(
              node.attribs['data-cke-saved-href'] || node.attribs.href,
              props.pageContent?.webUrl,
            );

            if (!url) {
              logger.debug(
                `Cannot get correct url for SharePoint link preview : ${
                  node.attribs['data-cke-saved-href'] || node.attribs.href
                }`,
              );

              return <span>{domToReact(node.children, sharePointOptions)}</span>;
            } else {
              return (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {domToReact(node.children, sharePointOptions)}
                </a>
              );
            }
          } else {
            return <span>{domToReact(node.children, sharePointOptions)}</span>;
          }
        }

        if (props.pageContent && props.siteId) {
          const image = renderInlineImage(node, props.pageContent, props.siteId);
          if (image) return image;
        }
      },
    }),
    [props.enableLinks, props.pageContent, props.siteId],
  );

  //Replace all links <a> with <span> when they point to SharePoint or are invalid
  //Also remove any url query params to prevent logging in the wrong tenant/OU
  const previewOptions: HTMLReactParserOptions = useMemo(
    () => ({
      replace: (domNode) => {
        const node = domNode as Element;
        if (node.name === 'a') {
          let url = node.attribs['data-cke-saved-href'] || node.attribs.href;
          const partRef = 'sharepoint.com';
          //disable all invalid URL's and URL's to SharePoint
          if (url && url.startsWith('https:') && !url.includes(partRef)) {
            //remove any params
            const urlParamIdx = url.indexOf('?');
            if (urlParamIdx >= 0) {
              url = url.substring(0, urlParamIdx);
            }

            return (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {domToReact(node.children, previewOptions)}
              </a>
            );
          } else {
            return <span>{domToReact(node.children, previewOptions)}</span>;
          }
        }

        if (props.pageContent && props.siteId) {
          const image = renderInlineImage(node, props.pageContent, props.siteId);
          if (image) return image;
        }
      },
    }),
    [props.pageContent, props.siteId],
  );

  //Main render
  // Sanitize before parsing: web part HTML is semi-trusted SharePoint content and, in the page editor, can now
  // be authored by the AI agent, so strip scripts / event handlers / javascript: URLs before rendering.
  const options = props.previewLinks ? previewOptions : sharePointOptions;
  const output = parse(sanitizeHtml(props.webPart.innerHtml ?? ''), options);

  return <div>{output}</div>;
};
